/**
 * Microsite Provider Selection Handler
 * Manages provider selection by setting DTC cookies and updating button state
 */
class MicrositesHandler {
  constructor() {
    this.selectButton = null;
    this.programsId = null;
    this.locationId = null;
    this.buttonContent = null;
    this.init();
  }

  /**
   * Initialize the handler
   */
  init() {
    // Wait for DOM to be ready
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => this.setup());
    } else {
      this.setup();
    }
  }

  /**
   * Setup button and event listeners
   */
  setup() {
    // Find the microsite select button
    this.selectButton = document.querySelector("[data-microsite-select-btn]");

    if (!this.selectButton) {
      console.log("Microsites: No select button found on this page");
      return;
    }

    this.buttonContent = this.selectButton.innerHTML;
    // Get programs_id and location_id from button data attributes
    this.programsId = this.selectButton.dataset.programsId;
    this.locationId = this.selectButton.dataset.locationId;

    if (!this.programsId || !this.locationId) {
      console.warn(
        "Microsites: Button missing programs_id or location_id data attributes",
      );
      return;
    }

    console.log(
      `Microsites: Initialized with programs_id=${this.programsId}, location_id=${this.locationId}`,
    );

    // Format phone numbers
    this.formatPhoneNumbers();

    // Auto-select provider on microsite page load
    this.autoSelectProvider();

    // Check if this provider is already selected
    this.updateButtonState();

    // Add click handler
    this.selectButton.addEventListener("click", (e) =>
      this.handleButtonClick(e),
    );

    // Listen for cookie changes
    window.addEventListener("dtcCookieSet", () => this.updateButtonState());
  }

  /**
   * Format US phone numbers to (XXX) XXX-XXXX
   */
  formatPhoneNumbers() {
    document.querySelectorAll('[data-phone-format]').forEach(function(el) {
      var digits = el.textContent.trim().replace(/\D/g, '');
      if (digits.length === 11 && digits[0] === '1') {
        el.textContent = '(' + digits.slice(1,4) + ') ' + digits.slice(4,7) + '-' + digits.slice(7);
      } else if (digits.length === 10) {
        el.textContent = '(' + digits.slice(0,3) + ') ' + digits.slice(3,6) + '-' + digits.slice(6);
      }
    });
  }

  /**
   * Build a provider payload from microsite metadata so cart UI can render it.
   * @returns {Record<string, string>|null}
   */
  buildProviderData() {
    if (!this.selectButton || !this.locationId) {
      return null;
    }

    const providerName = this.selectButton.dataset.providerName?.trim();
    const providerAddress = this.selectButton.dataset.providerAddress?.trim();

    if (!providerName || !providerAddress) {
      return null;
    }

    const providerData = {
      id: this.locationId,
      location_name: providerName,
      address: providerAddress,
    };

    const providerPhone = this.selectButton.dataset.providerPhone?.trim();
    const providerWebsite = this.selectButton.dataset.providerWebsite?.trim();

    if (providerPhone) {
      providerData.phone = providerPhone;
    }

    if (providerWebsite) {
      providerData.location_website = providerWebsite;
    }

    return providerData;
  }

  /**
   * Apply provider selection and persist provider metadata when available.
   * @param {string} programId
   * @param {string} source
   */
  applyProviderSelection(programId, source) {
    const providerData = this.buildProviderData();

    if (
      providerData &&
      typeof window.DTCCookieManager.assignProviderSelection === "function"
    ) {
      window.DTCCookieManager.assignProviderSelection(providerData, {
        priorityOverride: "1",
        dtc: true,
        source,
      });
      window.DTCCookieManager.setProgramId(programId);
      return;
    }

    window.DTCCookieManager.setLocationId(this.locationId);
    window.DTCCookieManager.setProgramId(programId);
    window.DTCCookieManager.setDTC(true);
  }

  /**
   * Auto-select provider on microsite page load.
   * Uses programId from URL param if valid, otherwise defaults to primary program ID.
   */
  autoSelectProvider() {
    if (!window.DTCCookieManager) return;

    const programIds = this.programsId.split(",").map((id) => id.trim());
    const params = new URLSearchParams(window.location.search);
    const urlProgramId = params.get("programId");

    // Use URL param if it matches one of this provider's programs, otherwise use the first one
    const selectedProgramId = urlProgramId && programIds.includes(urlProgramId)
      ? urlProgramId
      : programIds[0];

    // Skip if already selected with same values
    const currentLocationId = window.DTCCookieManager.getCookie("location_id");
    const currentProgramId = window.DTCCookieManager.getCookie("program_id");
    if (currentLocationId === this.locationId && currentProgramId === selectedProgramId) return;

    console.log(`Microsites: Auto-selecting provider — location_id=${this.locationId}, program_id=${selectedProgramId}`);

    this.applyProviderSelection(selectedProgramId, "microsite-auto");
  }

  /**
   * Check if this specific provider is currently selected
   */
  isThisProviderSelected() {
    if (!window.DTCCookieManager) return false;
    const currentLocationId = window.DTCCookieManager.getCookie("location_id");
    const currentProgramId = window.DTCCookieManager.getCookie("program_id");
    const programIds = this.programsId.split(",").map((id) => id.trim());
    return currentLocationId === this.locationId && programIds.includes(currentProgramId);
  }

  /**
   * Handle button click to set provider cookies
   * @param {Event} e - Click event
   */
  handleButtonClick(e) {
    e.preventDefault();

    // Check if DTCCookieManager is available
    if (!window.DTCCookieManager) {
      console.error("Microsites: DTCCookieManager not available");
      return;
    }

    console.log(
      `Microsites: Setting provider - programs_id=${this.programsId}, location_id=${this.locationId}`,
    );

    // Set the cookies using DTCCookieManager
    // Parse programs_id and set the first one (comma-separated list)
    const programIds = this.programsId.split(",").map((id) => id.trim());
    const primaryProgramId = programIds[0];

    this.applyProviderSelection(primaryProgramId, "microsite-button");

    // Get the button link and redirect if provided
    const buttonLink = this.selectButton.getAttribute("href");
    if (buttonLink && buttonLink !== "#" && buttonLink !== "") {
      window.location.href = buttonLink;
    } else {
      // If no link, just update the button state
      this.updateButtonState();
    }
  }

  /**
   * Update button state based on current cookies
   */
  updateButtonState() {
    if (!this.selectButton || !window.DTCCookieManager) {
      return;
    }

    const currentLocationId = window.DTCCookieManager.getCookie("location_id");
    const currentProgramId = window.DTCCookieManager.getCookie("program_id");

    // Parse programs_id (comma-separated list) and check if current program_id is in the list
    const programIds = this.programsId.split(",").map((id) => id.trim());
    const isSelected =
      currentLocationId === this.locationId &&
      programIds.includes(currentProgramId);

    if (isSelected) {
      this.selectButton.classList.add("selected");
      console.log("Microsites: Provider is selected");
      this.selectButton.setAttribute("aria-pressed", "true");
      this.selectButton.innerHTML = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 6px;"><path d="M20 6L9 17l-5-5"/></svg>This is your provider';
    } else {
      this.selectButton.classList.remove("selected");
      this.selectButton.setAttribute("aria-pressed", "false");
      this.buttonContent && (this.selectButton.innerHTML = this.buttonContent);
    }
  }
}

// Initialize when script loads
new MicrositesHandler();
